import React, { useEffect, useRef } from "react";
import { Button, Form, Input, InputGroup, InputGroupText, Spinner } from "reactstrap";
import useScanDetection from "use-scan-detection-react18";

// Minimum gap between two identical barcodes to prevent accidental double-submit.
const DUPLICATE_GUARD_MS = 800;

// The scan-detection library records event.key values, not characters.
// Hardware scanners send Shift keydown events before every uppercase letter
// (and for symbols like *@# that need Shift), so the raw buffer ends up as
// "ShiftAShiftB12" instead of "AB12".  Strip all modifier/special key names
// before using the code.
const KEY_NAME_RE = /Shift|Control|Alt|Meta|CapsLock|Tab|Backspace|Delete|Escape|Insert|Home|End|PageUp|PageDown|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|NumLock|ScrollLock|Pause|PrintScreen|ContextMenu|F\d{1,2}/g;

function stripKeyNames(raw) {
  return String(raw || "").replace(KEY_NAME_RE, "").trim();
}

/**
 * Mobile-optimised scan input for stock check sessions.
 *
 * Works with:
 *   • Hardware barcode scanners (via use-scan-detection-react18 global listener)
 *   • Manual keyboard entry + "Scan" button / Enter key
 *
 * Props
 *   onScan(barcode)  — called when a barcode is ready to submit
 *   isLoading        — disables the input during API call
 *   disabled         — disables the input entirely (e.g. closed session)
 *   autoFocus        — re-focus after scan (default: true)
 */
function StockCheckScanInput({ onScan, isLoading = false, disabled = false, autoFocus = true }) {
  const inputRef    = useRef(null);
  const lastScanRef = useRef({ barcode: null, at: 0 });
  const [value, setValue] = React.useState("");

  // ── Duplicate guard helper ────────────────────────────────────────────────
  const shouldSubmit = (barcode) => {
    const now = Date.now();
    const { barcode: lastBarcode, at: lastAt } = lastScanRef.current;
    if (barcode === lastBarcode && now - lastAt < DUPLICATE_GUARD_MS) {
      return false;
    }
    lastScanRef.current = { barcode, at: now };
    return true;
  };

  // ── Hardware scanner — fires on rapid char sequences ending with Enter ───
  // stripKeyNames() removes "Shift", "CapsLock", etc. that some scanner
  // firmware injects as raw event.key names into the buffer.
  useScanDetection({
    onComplete: (code) => {
      const cleaned = stripKeyNames(code);
      if (cleaned && !disabled && !isLoading && shouldSubmit(cleaned)) {
        onScan(cleaned);
        setValue("");
        if (autoFocus) {
          setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select?.(); }, 50);
        }
      }
    },
    minLength: 2,
  });

  // ── Auto-focus on mount ───────────────────────────────────────────────────
  useEffect(() => {
    if (autoFocus && !disabled) {
      const t = setTimeout(() => { inputRef.current?.focus(); }, 50);
      return () => clearTimeout(t);
    }
  }, [autoFocus, disabled]);

  // ── Re-focus after loading finishes (API call returned) ───────────────────
  useEffect(() => {
    if (!isLoading && autoFocus && !disabled) {
      setTimeout(() => { inputRef.current?.focus(); }, 50);
    }
  }, [isLoading, autoFocus, disabled]);

  // ── Manual submit ─────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || disabled || isLoading) return;
    if (!shouldSubmit(trimmed)) return;
    onScan(trimmed);
    setValue("");
    setTimeout(() => { inputRef.current?.focus(); }, 50);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <InputGroup className="sc-scan-group">
        <InputGroupText>
          <i className="tim-icons icon-zoom-split" />
        </InputGroupText>
        <Input
          innerRef={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Scan or type barcode…"
          disabled={disabled || isLoading}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <Button
          type="submit"
          color="primary"
          disabled={disabled || isLoading || !value.trim()}
        >
          {isLoading ? <Spinner size="sm" /> : "Scan"}
        </Button>
      </InputGroup>
    </Form>
  );
}

export default StockCheckScanInput;
